// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChangePasswordModalResult } from '@interfaces/dashboard.interfaces';
import { User } from '@interfaces/user.interfaces';
import { UserService } from '@services/user.service';
import { takeWhile } from 'rxjs';
import { currentPasswordErrorControlValidator, passwordMatch, passwordPattern, showFormErrors } from 'src/app/utils/form-utils';

@Component({
    selector: 'change-password-modal',
    templateUrl: './change-password-modal.component.html',
    styleUrls: ['./change-password-modal.component.scss'],
})
export class ChangePasswordModalComponent implements OnInit {
    isLoading = false;
    isComponentAlive = true;
    newPasswordForm: FormGroup;
    viewCurrentPassword = false;
    viewPassword = false;
    viewPassword2 = false;
    temporaryPassword: boolean = false;
    title: string = 'Create a new password';
    passwordInputLabel: string = 'New Password';
    passwordRequirements = {
        oldPassword: false,
        hasLower: false,
        hasUpper: false,
        hasNumber: false,
        hasSpecialChar: false,
        hasMinLength: false,
        bothMatch: false,
    };
    currentPasswordError: string = '';

    constructor(
        private dialogRef: MatDialogRef<ChangePasswordModalComponent, ChangePasswordModalResult>,
        private readonly userService: UserService,
        @Inject(MAT_DIALOG_DATA) public data: { user: User; temporaryPassword?: boolean }
    ) {
        if (data?.temporaryPassword) {
            this.temporaryPassword = data.temporaryPassword;
            this.title = 'Set Temporary Password';
            this.passwordInputLabel = 'Temporary Password';
            this.viewPassword = true;
        }
    }

    ngOnInit(): void {
        this.newPasswordForm = this.temporaryPassword
            ? new FormGroup({
                  password: new FormControl('', [Validators.required, Validators.pattern(passwordPattern)]),
              })
            : new FormGroup(
                  {
                      currentPassword: new FormControl('', [Validators.required, currentPasswordErrorControlValidator(this)]),
                      password: new FormControl('', [Validators.required, Validators.pattern(passwordPattern)]),
                      password2: new FormControl('', [Validators.required]),
                  },
                  {
                      validators: passwordMatch,
                  }
              );
    }

    cancel(): void {
        this.dialogRef.close({ currentPassword: null, newPassword: null });
    }

    newPasswordSubmit(): void {
        if (this.newPasswordForm.status === 'INVALID' && !this.newPasswordForm.controls.currentPassword.errors?.currentPasswordError) {
            showFormErrors(this.newPasswordForm.controls);
            return;
        }

        const currentPassword = this.temporaryPassword ? null : this.newPasswordForm.controls.currentPassword.value;
        const newPassword = this.newPasswordForm.controls.password.value;

        if (currentPassword && newPassword) {
            this.isLoading = true;
            this.userService
                .profileChangePassword(this.data.user, currentPassword, newPassword)
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe(({ success, errorMsg }) => {
                    this.isLoading = false;
                    if (success) {
                        this.dialogRef.close({ currentPassword, newPassword });
                    } else {
                        this.currentPasswordError = errorMsg;
                        this.newPasswordForm.controls.currentPassword.updateValueAndValidity();
                    }
                });
        } else {
            this.dialogRef.close({ currentPassword, newPassword });
        }
    }

    public passwordVisible(inputField: 'viewCurrentPassword' | 'viewPassword' | 'viewPassword2'): void {
        this[inputField] = !this[inputField];
    }
}
